import { getContactDocumentName } from '../../utils/contactDisplayName';
import { buildReceiptItemDescription } from './receiptItemDisplay';

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatThaiDate = (value, dayjs) => {
  const date = dayjs(value);
  if (!date.isValid()) return '-';
  return `${date.format('DD/MM')}/${date.year() + 543}`;
};

const borderedLayout = {
  hLineWidth: () => 0.7,
  vLineWidth: () => 0.7,
  hLineColor: () => '#94a3b8',
  vLineColor: () => '#94a3b8',
  paddingLeft: () => 7,
  paddingRight: () => 7,
  paddingTop: () => 6,
  paddingBottom: () => 6
};

export const createDepositReceiptDefinition = ({
  company = {},
  receipt = {},
  depositPayment = {},
  logoBase64,
  fixedSignatureLogo,
  fixedStampLogo,
  signatureLayout = { x: 0, y: 0, width: 100 },
  printOptions = {},
  numberToThaiText,
  formatCurrency,
  dayjs
}) => {
  const depositAmount = Math.max(0, toNumber(depositPayment.amount));
  const totalAmount = Math.max(0, toNumber(depositPayment.total_amount_snapshot ?? receipt.total_amount));
  const paidBefore = Math.max(0, toNumber(
    depositPayment.paid_before_snapshot ?? (toNumber(receipt.paid_amount) - depositAmount)
  ));
  const paidAfter = Math.min(totalAmount, paidBefore + depositAmount);
  const remainingAmount = Math.max(0, toNumber(
    depositPayment.remaining_after_snapshot ?? (totalAmount - paidAfter)
  ));
  const customerName = getContactDocumentName(receipt);
  const collectorName = printOptions.collector_name
    || company.signature_name
    || receipt.created_by_name
    || 'ผู้รับเงิน';
  const documentNumber = depositPayment.deposit_receipt_number || '-';
  const itemSummary = (Array.isArray(receipt.items) ? receipt.items : [])
    .map(buildReceiptItemDescription)
    .filter(Boolean)
    .slice(0, 4)
    .join(', ') || receipt.notes || 'รับเงินมัดจำค่าสินค้า/บริการ';
  const signatureWidth = Math.min(Math.max(toNumber(signatureLayout.width) || 100, 40), 160);

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [32, 26, 32, 28],
    defaultStyle: {
      font: 'THSarabunNew',
      fontSize: 12,
      color: '#172033',
      lineHeight: 0.95
    },
    styles: {
      companyName: { fontSize: 18, bold: true, color: '#0f2748' },
      title: { fontSize: 20, bold: true, alignment: 'center', color: '#0b63ce' },
      subtitle: { fontSize: 10, alignment: 'center', color: '#64748b' },
      label: { fontSize: 10, color: '#64748b' },
      value: { fontSize: 12, bold: true },
      money: { fontSize: 14, bold: true, alignment: 'right' },
      remaining: { fontSize: 20, bold: true, alignment: 'right', color: '#c2410c' }
    },
    content: [
      {
        columns: [
          {
            width: '62%',
            columns: [
              logoBase64
                ? { width: 78, image: logoBase64, fit: [66, 66], margin: [0, 0, 8, 0] }
                : { width: 18, text: '' },
              {
                width: '*',
                stack: [
                  { text: company.company_name || company.name || 'ชื่อกิจการ', style: 'companyName' },
                  { text: company.address || '-', margin: [0, 3, 0, 0] },
                  { text: `โทร ${company.phone || '-'}  เลขประจำตัวผู้เสียภาษี ${company.tax_id || '-'}` }
                ]
              }
            ]
          },
          {
            width: '38%',
            table: {
              widths: ['*'],
              body: [[{
                stack: [
                  { text: 'ใบเสร็จรับเงินมัดจำ', style: 'title' },
                  { text: 'DEPOSIT RECEIPT', style: 'subtitle' }
                ],
                fillColor: '#eff6ff',
                margin: [4, 8, 4, 8]
              }]]
            },
            layout: borderedLayout
          }
        ],
        margin: [0, 0, 0, 14]
      },
      {
        table: {
          widths: ['56%', '44%'],
          body: [[
            {
              stack: [
                { text: 'ลูกค้า / CUSTOMER', style: 'label' },
                { text: customerName, style: 'value', margin: [0, 2, 0, 3] },
                { text: `เลขประจำตัวผู้เสียภาษี ${receipt.tax_id || '-'}` },
                { text: `ที่อยู่ ${receipt.address || '-'}` },
                { text: `โทร ${receipt.phone || '-'}` }
              ]
            },
            {
              stack: [
                { text: 'เลขที่ใบเสร็จมัดจำ / DEPOSIT NO.', style: 'label' },
                { text: documentNumber, style: 'value', margin: [0, 2, 0, 3] },
                ...(printOptions.show_document_date !== false
                  ? [{ text: `วันที่รับเงิน ${formatThaiDate(depositPayment.payment_date, dayjs)}` }]
                  : []),
                { text: `อ้างอิงใบแจ้งหนี้ ${receipt.invoice_number || '-'}` },
                { text: `กำหนดชำระ ${formatThaiDate(receipt.due_date, dayjs)}` }
              ]
            }
          ]]
        },
        layout: borderedLayout,
        margin: [0, 0, 0, 14]
      },
      {
        table: {
          headerRows: 1,
          widths: [42, '*', 105],
          body: [
            [
              { text: 'ลำดับ', bold: true, alignment: 'center', fillColor: '#e2e8f0' },
              { text: 'รายการรับเงิน', bold: true, alignment: 'center', fillColor: '#e2e8f0' },
              { text: 'จำนวนเงิน', bold: true, alignment: 'center', fillColor: '#e2e8f0' }
            ],
            [
              { text: '1', alignment: 'center', margin: [0, 10, 0, 10] },
              {
                stack: [
                  { text: `รับเงินมัดจำตามใบแจ้งหนี้ ${receipt.invoice_number || '-'}`, bold: true },
                  { text: itemSummary, color: '#475569', margin: [0, 3, 0, 0] }
                ],
                margin: [0, 6, 0, 6]
              },
              { text: formatCurrency(depositAmount), style: 'money', margin: [0, 10, 0, 10] }
            ]
          ]
        },
        layout: borderedLayout,
        margin: [0, 0, 0, 12]
      },
      {
        columns: [
          {
            width: '48%',
            table: {
              widths: ['*'],
              body: [[{
                stack: [
                  { text: 'รายละเอียดการรับเงิน', bold: true, color: '#0b63ce' },
                  { text: `วิธีรับชำระ: ${depositPayment.payment_method || '-'}`, margin: [0, 5, 0, 0] },
                  { text: `ธนาคาร: ${depositPayment.bank_name || '-'}` },
                  { text: `เลขที่อ้างอิง: ${depositPayment.reference_no || '-'}` },
                  { text: `หมายเหตุ: ${depositPayment.notes || '-'}` }
                ],
                margin: [3, 3, 3, 3]
              }]]
            },
            layout: borderedLayout
          },
          { width: '4%', text: '' },
          {
            width: '48%',
            table: {
              widths: ['60%', '40%'],
              body: [
                [{ text: 'ยอดรวมตามใบแจ้งหนี้' }, { text: formatCurrency(totalAmount), alignment: 'right', bold: true }],
                [{ text: 'รับชำระก่อนหน้า' }, { text: formatCurrency(paidBefore), alignment: 'right' }],
                [{ text: 'เงินมัดจำครั้งนี้', bold: true, color: '#0b63ce' }, { text: formatCurrency(depositAmount), alignment: 'right', bold: true, color: '#0b63ce' }],
                [{ text: 'รับชำระสะสม' }, { text: formatCurrency(paidAfter), alignment: 'right', bold: true }]
              ]
            },
            layout: borderedLayout
          }
        ],
        margin: [0, 0, 0, 12]
      },
      {
        table: {
          widths: ['62%', '38%'],
          body: [[
            {
              stack: [
                { text: `(${numberToThaiText(depositAmount)})`, bold: true, alignment: 'center' },
                { text: 'จำนวนเงินมัดจำที่ได้รับในครั้งนี้', style: 'label', alignment: 'center', margin: [0, 3, 0, 0] }
              ],
              fillColor: '#eff6ff',
              margin: [4, 8, 4, 8]
            },
            {
              stack: [
                { text: 'ยอดค้างชำระส่วนที่เหลือ', bold: true, alignment: 'right' },
                { text: formatCurrency(remainingAmount), style: 'remaining' }
              ],
              fillColor: '#fff7ed',
              margin: [4, 5, 4, 5]
            }
          ]]
        },
        layout: borderedLayout,
        margin: [0, 0, 0, 18]
      },
      {
        columns: [
          {
            width: '48%',
            stack: [
              { text: 'ลงชื่อผู้ชำระเงิน', alignment: 'center' },
              { text: '_________________________________', alignment: 'center', margin: [0, 28, 0, 2] },
              { text: customerName, alignment: 'center' }
            ]
          },
          { width: '4%', text: '' },
          {
            width: '48%',
            stack: [
              { text: 'ลงชื่อผู้รับเงิน', alignment: 'center' },
              ...(fixedSignatureLogo ? [{
                image: fixedSignatureLogo,
                width: signatureWidth,
                alignment: 'center',
                relativePosition: { x: toNumber(signatureLayout.x), y: toNumber(signatureLayout.y) },
                margin: [0, 0, 0, -Math.round(signatureWidth * 0.32)]
              }] : []),
              { text: '_________________________________', alignment: 'center', margin: [0, fixedSignatureLogo ? 16 : 28, 0, 2] },
              { text: collectorName, alignment: 'center' }
            ]
          }
        ],
        margin: [0, 10, 0, 0]
      },
      ...(fixedStampLogo ? [{
        image: fixedStampLogo,
        width: 64,
        alignment: 'right',
        margin: [0, -58, 20, 0]
      }] : []),
      {
        text: 'เอกสารนี้เป็นหลักฐานการรับเงินมัดจำ ยอดค้างชำระจะเปลี่ยนเมื่อมีการรับชำระครั้งถัดไป',
        fontSize: 9,
        color: '#64748b',
        alignment: 'center',
        margin: [0, 18, 0, 0]
      }
    ]
  };
};
